import addressSvg from '@/assets/icons/address.svg'
import emailSvg from '@/assets/icons/email.svg'
import facebookSvg from '@/assets/icons/social/facebook.svg'
import instagramSvg from '@/assets/icons/social/instagram.svg'
import linkedinSvg from '@/assets/icons/social/linkedin.svg'
import xSvg from '@/assets/icons/social/x.svg'
import successSvg from '@/assets/icons/success.svg'
import AutoGrowTextarea from '@/components/AutoGrow/AutoGrowTextarea'
import Checkbox from '@/components/Checkbox/Checkbox'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import {ADDRESS, EMAIL, SOCIAL_MEDIA} from '@/constants/contacts'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import {HomeRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'

import style from './ContactUs.module.scss'

export default memo(function ContactUs() {
  const [isAgreed, setIsAgreed] = useState(true)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  const submitContactForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (e.currentTarget.checkValidity()) {
      call
        .post('/api/feedback/', {
          data: {
            feedback_type: 'form',
            email,
            phone,
            full_name: name,
            content: message,
          },
        })
        .then(() => {
          setIsSuccess(true)
        })
        .catch(e => {
          if (shouldLogError(e)) {
            console.error('Cannot send feedback', e)
          }
        })
    }
  }

  const latestHandleMessageChange = useStableCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    e => {
      setMessage(e.currentTarget.value)
    },
  )

  return (
    <>
      <HeadTags title='Contact us' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar variant='dark' />
      <main className={style.ContactUs} {...skipTargetProps('main-content')}>
        <h1 className={style.Headline}>
          Ge<span className={style.Highlighted}>t in to</span>uch
        </h1>
        <div className={style.Container}>
          <div className={style.ContactInformation}>
            <div className={style.Title}>Contact Information</div>
            <div className={style.SubText}>
              Leave us a message and we&apos;ll get back to you soon.
            </div>
            <div className={style.ContactContainer} role='list' aria-label='Contact info'>
              <div className={style.Contact} role='listitem'>
                <div>
                  <img src={emailSvg} alt='email' aria-hidden='true' />
                </div>
                <div>
                  <VisuallyHidden>Email:</VisuallyHidden>
                  {EMAIL}
                </div>
              </div>
              <div className={style.Contact} role='listitem'>
                <div>
                  <img src={addressSvg} alt='address' aria-hidden='true' />
                </div>
                <div>
                  <VisuallyHidden>Address:</VisuallyHidden>
                  {ADDRESS}
                </div>
              </div>
            </div>
            <footer>
              <div className={style.Social} role='list' aria-label='Social Links'>
                <div role='listitem'>
                  <Link to={SOCIAL_MEDIA.linkedin} target='_blank' rel='noreferrer noopener'>
                    <img src={linkedinSvg} alt="Innosearch's linkedin" />
                  </Link>
                </div>
                <div role='listitem'>
                  <Link to={SOCIAL_MEDIA.facebook} target='_blank' rel='noreferrer noopener'>
                    <img src={facebookSvg} alt="Innosearch's facebook" />
                  </Link>
                </div>
                <div role='listitem'>
                  <Link to={SOCIAL_MEDIA.instagram} target='_blank' rel='noreferrer noopener'>
                    <img src={instagramSvg} alt="Innosearch's instagram" />
                  </Link>
                </div>
                <div role='listitem'>
                  <Link to={SOCIAL_MEDIA.x} target='_blank' rel='noreferrer noopener'>
                    <img src={xSvg} alt="Innosearch's x (twitter)" />
                  </Link>
                </div>
              </div>
            </footer>
          </div>
          {!isSuccess ? (
            <form className={style.Form} onSubmit={submitContactForm}>
              <div className={style.Section1}>
                <div className={style.FormItem}>
                  <div className={style.FormName}>What is your name?</div>
                  <div>
                    <input
                      placeholder='Full name'
                      onChange={e => {
                        setName(e.currentTarget.value)
                      }}
                      required
                    />
                  </div>
                </div>
                <div className={style.FormItem}>
                  <div className={style.FormName}>What’s your email address?</div>
                  <div>
                    <input
                      placeholder='Email address'
                      onChange={e => {
                        setEmail(e.currentTarget.value)
                      }}
                      type='email'
                      required
                    />
                  </div>
                </div>
              </div>
              <div className={style.Section2}>
                <div className={style.FormName}>What’s your phone number? (optional)</div>
                <input
                  placeholder='Phone number'
                  onChange={e => {
                    setPhone(e.currentTarget.value)
                  }}
                  type='phone'
                />
              </div>
              <div className={style.Section2}>
                <div className={style.FormName}>What’s your message?</div>
                <AutoGrowTextarea
                  placeholder='Message'
                  onChange={latestHandleMessageChange}
                  required
                  rows={4}
                />
              </div>
              <div>
                <Checkbox
                  label='By checking this box, you agree to receive text and email messages from Innosearch, and that Innosearch will process my personal information in accordance with their Privacy Policy. Recurring messages subscription. Messages and data rates may apply. For text, reply HELP for help and STOP to opt out.'
                  isLabelVisible
                  isSelected={isAgreed}
                  onChange={setIsAgreed}
                  className={style.Note}
                />
              </div>
              <div className={style.Buttons}>
                <PrimaryButton type='submit' className={style.Submit} isDisabled={!isAgreed}>
                  Submit
                </PrimaryButton>
              </div>
            </form>
          ) : (
            <div className={style.SuccessPanel}>
              <div>
                <img src={successSvg} alt='message sent' />
              </div>
              <div className={style.Message} role='alert'>
                Your information has been submitted
              </div>
              <Link to={HomeRoute.fullPath} className={style.GoBack}>
                Back to home page
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  )
})
